// App.js — Better-than-FAANG+ routing with consistent protected redirects
// React Router v5.2 — ZERO regression to existing behavior.
// Key upgrade: all protected routes redirect to /login?from=<full-url> and return post-login.

import Home from './pages/home/Home';
import PostPage from './pages/post/PostPage';
import MemePage from './pages/memePage/memePage';
import Galaxies from './pages/galaxy/galaxy';
import GalaxyProfile from './pages/galaxyProfile/GalaxyProfile';

import Activate from './pages/activate/Activate';
import Login from './pages/login/Login';
import Profile from './pages/profile/Profile';
import Register from './pages/register/Register';
import Messenger from './pages/messenger/Messenger';
import Advertise from './pages/advertise/Advertise';
import JGKToken from './pages/jgkToken/JGKToken';
import JGKCashoutAdmin from './pages/JGKCashoutAdmin';
import Meme from './pages/meme/Meme';
import PlayerMap from './pages/playerMap/PlayerMap';
import Reset from './pages/reset/Reset';
import WindowFocusHandler from './windowFocusHandler';
import checkJWTExpiration from './checkJWTExpiration';
import FeedbackForm from './pages/feedback/Feedback';
import Airtime from './pages/airtime/Airtime';
import LandingPage from './pages/landingPage/LandingPage';
import LocalOpsContinue from './pages/localopsContinue/LocalOpsContinue';
import LocalOpsOpsHealth from './pages/localopsOps/LocalOpsOpsHealth';
import LocalOpsProofsAdmin from './pages/localopsProofsAdmin/LocalOpsProofsAdmin';
import LocalOpsConcierge from './pages/localopsConcierge/LocalOpsConcierge';
import LocalOpsOfferingConcierge from './pages/localopsConcierge/LocalOpsOfferingConcierge';
import LocalOpsRegionLanding from './pages/localopsRegion/LocalOpsRegionLanding';
import LocalOpsHowItWorks from './pages/localopsHowItWorks/LocalOpsHowItWorks';
import ShareLanding from './pages/share/ShareLanding';
import LocalOpsProofLanding from './pages/localopsProof/LocalOpsProofLanding';
import LocalOpsProofPortfolio from './pages/localopsProofPortfolio/LocalOpsProofPortfolio';
import HuntListPage from './pages/huntlistPage/HuntListPage';
import HuntDetailPage from './pages/huntDetailPage/huntDetailPage';
import BusinessDirectory from './pages/businessDirectory/BusinessDirectory';
import BusinessPage from './pages/businessPage/BusinessPage';
import CheckinHeatmapPage from './pages/checkinHeatmapPage/CheckinHeatmapPage';
import BusinessDashboard from './pages/businessDashboard/BusinessDashboard';
import PresenceAdmin from './pages/PresenceAdmin';
import PresenceDirectory from './pages/PresenceDirectory';
import PresenceDemo from './pages/PresenceDemo';
import PresenceOnboarding from './pages/PresenceOnboarding';
import PresenceIntegration from './pages/PresenceIntegration';
import Contact from './pages/Contact';
import Docs from './pages/docs/Docs';
import Quickstart from './pages/docs/Quickstart';
import Pricing from './pages/docs/Pricing';
import PresenceOnlineOffline from './pages/docs/PresenceOnlineOffline';
import WhosOnlineApi from './pages/docs/WhosOnlineApi';
import PresenceSocketIo from './pages/docs/PresenceSocketIo';
import WebRTCPresence from './pages/docs/WebRTCPresence';
import MultiTabPresence from './pages/docs/patterns/MultiTabPresence';
import IdleDetection from './pages/docs/patterns/IdleDetection';
import ReconnectHandling from './pages/docs/patterns/ReconnectHandling';
import SessionVsUser from './pages/docs/patterns/SessionVsUser';
import LocalOpsDisplayMode from './components/localops/LocalOpsDisplayMode';
import PostCompletionPreviewPage from './components/localops/PostCompletionPreviewPage';
import ResumeGate from './components/resume/ResumeGate';

import { Switch, Route, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';
import { AuthContext } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import TrailblazerNav from './components/trailblazerNav/TrailblazerNav';
import LegacyNav from './components/legacyNav/LegacyNav';

// Badges
import BadgesPage from './pages/badgesPage/badgesPage'; // /badges (current user)
import BadgePage from './pages/badgePage/BadgePage'; // /p/:playerId/badges (deep links / detail page)
import OrbitingFriends from './components/orbitingFriends/OrbitingFriends';
import { initPresence } from './utils/presenceManager';
import { useSocket } from './context/socket';
import { isSafeAuthReturnTo } from './utils/authReturnTo';
import {
  AUTH_EXPIRED_EVENT,
  triggerAuthExpiredRecovery,
} from './utils/authSessionRecovery';
// ---------- Helper: consistent protected-route redirect ----------
function useFullPath() {
  const location = useLocation();
  // Preserve exact path, query, and hash
  return `${location.pathname}${location.search || ''}${location.hash || ''}`;
}

function ProtectedRoute({ component: Component, ...rest }) {
  const { player } = useContext(AuthContext);
  const fullPath = useFullPath();

  return (
    <Route
      {...rest}
      render={(props) =>
        player ? (
          <Component {...props} />
        ) : (
          <Redirect to={`/login?from=${encodeURIComponent(fullPath)}`} />
        )
      }
    />
  );
}
// -----------------------------------------------------------------

function App() {
  const { player, dispatch } = useContext(AuthContext);
  const [inFocus, setInFocus] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const from = localStorage.getItem('from');
  const uiPreviewEnabled =
    process.env.REACT_APP_UI_PREVIEW === '1' ||
    process.env.NODE_ENV === 'development';
  const { darkTheme } = useTheme();
  const { socket, getLatencyMs } = useSocket();

  // Routes where Trailblazer/Legacy nav should render (kept as-is to avoid regressions)
  const showNavPaths = [
    '/business-directory',
    '/business/',
    '/business-dashboard/',
    '/hunts',
    '/leaderboard',
    '/rewards',
    '/checkin-heatmap',
  ];
  const noNavPaths = ['/login', '/register', '/localops/live'];

  const showNav =
    showNavPaths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(path),
    ) ||
    noNavPaths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(path),
    );

  useEffect(() => {
    if (inFocus && player) {
      if (!checkJWTExpiration()) {
        triggerAuthExpiredRecovery({
          source: 'jwt-focus-check',
          reason: 'jwt-expired',
        });
      }
    }
  }, [inFocus, player]);

  useEffect(() => {
    const onAuthExpired = (event) => {
      dispatch?.({ type: 'AUTH_EXPIRED' });
      if (location.pathname === '/login') return;

      const currentPath = `${location.pathname}${location.search || ''}${
        location.hash || ''
      }`;
      const returnTo =
        event?.detail?.returnTo && isSafeAuthReturnTo(event.detail.returnTo)
          ? event.detail.returnTo
          : isSafeAuthReturnTo(currentPath)
            ? currentPath
            : null;
      const target = returnTo
        ? `/login?from=${encodeURIComponent(returnTo)}`
        : '/login';
      history.replace(target);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    };
  }, [
    dispatch,
    history,
    location.hash,
    location.pathname,
    location.search,
  ]);

  // Theme class sync (kept as-is)
  useEffect(() => {
    if (darkTheme) {
      document.body.classList.add('dark-theme');
      document.documentElement.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
      document.documentElement.classList.remove('dark-theme');
    }
  }, [darkTheme]);

  // GLOBAL PRESENCE INITIALIZER — runs once per login session
  useEffect(() => {
    if (!socket || !player?.data?.player?._id) return;

    // Start presence engine with identity of the logged-in player
    const cleanup = initPresence(socket, player.data.player._id, {
      getLatencyMs,
    });

    return cleanup; // ensures disconnect cleanly on logout/unmount
  }, [socket, player?.data?.player?._id, getLatencyMs]);

  // Normalize accidental double slashes to ensure routes match as expected
  if (location.pathname.includes('//')) {
    const normalizedPath = location.pathname.replace(/\/{2,}/g, '/');
    const target = `${normalizedPath}${location.search || ''}${location.hash || ''}`;
    return <Redirect to={target} />;
  }

  return (
    <>
      <WindowFocusHandler setInFocus={setInFocus} />

      {player &&
        !noNavPaths.some((path) => location.pathname.startsWith(path)) &&
        (showNav ? <TrailblazerNav /> : <LegacyNav />)}
      {player &&
        !location.pathname.startsWith('/playermap') &&
        !location.pathname.startsWith('/localops/ops') && <OrbitingFriends />}

      {/* Single continuation authority for LocalOps and future flows */}
      {player && <ResumeGate />}

      <div className="pageContent">
        <Switch>
          {/* Root: preserves your existing "from" behavior */}
          <Route exact path="/">
            {player ? (
              from !== null && from !== 'null' ? (
                <Redirect to={from} />
              ) : (
                <Home />
              )
            ) : (
              <Login />
            )}
          </Route>

          {/* PUBLIC pages (unchanged) */}
          <Route path="/trailblazer">
            <LandingPage />
          </Route>
          <Route exact path="/localops/live">
            <LocalOpsDisplayMode />
          </Route>
          {uiPreviewEnabled ? (
            <Route exact path="/_ui/localops/completion/:scenario">
              <PostCompletionPreviewPage />
            </Route>
          ) : null}
          {uiPreviewEnabled ? (
            <Route exact path="/_ui/localops/completion">
              <PostCompletionPreviewPage />
            </Route>
          ) : null}
          <Route path="/share/localops/proof/:proofId">
            <LocalOpsProofLanding />
          </Route>
          <Route exact path="/profile/:playerId/proofs">
            <LocalOpsProofPortfolio />
          </Route>
          <Route path="/share/offering/:id">
            <ShareLanding />
          </Route>
          <Route path="/share/task/:id">
            <ShareLanding />
          </Route>
          <Route path="/offerings/:offeringId">
            <ShareLanding />
          </Route>
          <Route path="/tasks/:taskId">
            <ShareLanding />
          </Route>
          <Route path="/localops/continue">
            <LocalOpsContinue />
          </Route>
          <Route exact path="/localops/how-it-works">
            <LocalOpsHowItWorks />
          </Route>
          <Route path="/localops/region/:slug">
            <LocalOpsRegionLanding />
          </Route>
          <Route path="/login">
            {player ? (
              (() => {
                const params = new URLSearchParams(location.search || '');
                const fromParam = params.get('from');
                const target =
                  fromParam && isSafeAuthReturnTo(fromParam) ? fromParam : '/';
                return (
                  <Redirect
                    to={
                      location.pathname !== '/login'
                        ? location.pathname
                        : target
                    }
                  />
                );
              })()
            ) : (
              <Login />
            )}
          </Route>
          <Route path="/register/:token">
            {player ? <Redirect to="/" /> : <Register />}
          </Route>
          <Route path="/reset">
            <Reset />
          </Route>
          <Route path="/presence-demo">
            <PresenceDemo />
          </Route>
          <Route path="/docs/pricing">
            <Pricing />
          </Route>
          <Route path="/docs/presence-online-offline">
            <PresenceOnlineOffline />
          </Route>
          <Route path="/docs/presence-online-offline.md">
            <PresenceOnlineOffline />
          </Route>
          <Route path="/docs/whos-online-api">
            <WhosOnlineApi />
          </Route>
          <Route path="/docs/whos-online-api.md">
            <WhosOnlineApi />
          </Route>
          <Route path="/docs/presence-socket-io">
            <PresenceSocketIo />
          </Route>
          <Route path="/docs/presence-socket-io.md">
            <PresenceSocketIo />
          </Route>
          <Route path="/docs/webrtc-presence">
            <WebRTCPresence />
          </Route>
          <Route path="/docs/webRTC-presence">
            <WebRTCPresence />
          </Route>
          <Route path="/docs/webrtc-presence.md">
            <WebRTCPresence />
          </Route>
          <Route path="/docs/patterns/multi-tab">
            <MultiTabPresence />
          </Route>
          <Route path="/docs/patterns/multi-tab.md">
            <MultiTabPresence />
          </Route>
          <Route path="/docs/patterns/idle-detection">
            <IdleDetection />
          </Route>
          <Route path="/docs/patterns/idle-detection.md">
            <IdleDetection />
          </Route>
          <Route path="/docs/patterns/reconnects">
            <ReconnectHandling />
          </Route>
          <Route path="/docs/patterns/reconnects.md">
            <ReconnectHandling />
          </Route>
          <Route path="/docs/patterns/session-vs-user">
            <SessionVsUser />
          </Route>
          <Route path="/docs/patterns/session-vs-user.md">
            <SessionVsUser />
          </Route>
          <Route path="/docs/quickstart">
            <Quickstart />
          </Route>
          <Route path="/docs/index.md">
            <Docs />
          </Route>
          <Route path="/docs">
            <Docs />
          </Route>

          {/* PROTECTED pages (now consistently redirect to /login?from=fullURL) */}
          <ProtectedRoute
            exact
            path="/galaxies/:galaxyId"
            component={GalaxyProfile}
          />
          <ProtectedRoute path="/galaxies" component={Galaxies} />

          <ProtectedRoute
            path="/business-directory"
            component={BusinessDirectory}
          />
          <ProtectedRoute
            path="/business/:businessId"
            component={BusinessPage}
          />
          <ProtectedRoute
            path="/checkin-heatmap"
            component={CheckinHeatmapPage}
          />
          <ProtectedRoute
            path="/business-dashboard/:businessId"
            component={BusinessDashboard}
          />
          <ProtectedRoute path="/presence-admin" component={PresenceAdmin} />
          <ProtectedRoute path="/presence-directory" component={PresenceDirectory} />
          <ProtectedRoute
            exact
            path="/presence/onboarding"
            component={PresenceOnboarding}
          />
          <ProtectedRoute
            exact
            path="/presence/integration"
            component={PresenceIntegration}
          />
          <ProtectedRoute exact path="/contact" component={Contact} />

          <ProtectedRoute exact path="/post/:postId" component={PostPage} />
          <ProtectedRoute exact path="/meme/:memeId" component={MemePage} />

          <ProtectedRoute path="/airtime" component={Airtime} />
          <ProtectedRoute path="/messenger/:convoId" component={Messenger} />

          <ProtectedRoute path="/profile/:playerId" component={Profile} />

          {/* List (player-scoped) */}
          <ProtectedRoute
            exact
            path="/p/:playerId/badges"
            component={BadgesPage}
          />
          {/* Detail (player-scoped + badge param) */}
          <ProtectedRoute
            exact
            path="/p/:playerId/badges/:badgeIdOrSlug"
            component={BadgePage}
          />

          {/* Keep the self route too: */}
          <ProtectedRoute exact path="/badges" component={BadgesPage} />

          <ProtectedRoute path="/advertise" component={Advertise} />

          <ProtectedRoute
            exact
            path="/hunts/:huntId"
            component={HuntDetailPage}
          />
          <ProtectedRoute path="/hunts" component={HuntListPage} />

          <ProtectedRoute path="/feedback" component={FeedbackForm} />
          <ProtectedRoute
            exact
            path="/jgkCashoutAdmin"
            component={JGKCashoutAdmin}
          />
          <ProtectedRoute path="/jgktoken" component={JGKToken} />
          <ProtectedRoute exact path="/localops/concierge" component={LocalOpsConcierge} />
          <ProtectedRoute exact path="/localops/concierge/offerings" component={LocalOpsOfferingConcierge} />
          <ProtectedRoute exact path="/localops/proofs" component={LocalOpsProofsAdmin} />
          <ProtectedRoute exact path="/localops/ops" component={LocalOpsOpsHealth} />
          <ProtectedRoute path="/meme" component={Meme} />
          <ProtectedRoute path="/playermap" component={PlayerMap} />

          {/* Keep Activate as protected (matches your current behavior) */}
          <Route path="/activate/:token">
            <Activate />
          </Route>

          {/* Fallback */}
          <Route path="*">{player ? <Home /> : <Register />}</Route>
        </Switch>
      </div>
    </>
  );
}

export default App;
